import styled from 'styled-components';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import {
  Spacings,
  Fonts,
  TextColors,
  PRIMARY_BORDER_WIDTH,
  SOLID_BORDER_STYLE,
  breakpoints
} from '@styles/index';

const getWidth = (grid, width) => {
  if (width) return width;
  if (grid === 2) return '80px';
  if (grid === 3) return '128px';
  if (grid === 4) return '176px';
  if (grid > 4) return '100%';
};

const getHeight = (grid, maxHeight) => {
  // use maxheight or use according to grid
  if (maxHeight === 'xs') return Spacings.SPACING_8B;
  if (maxHeight === 'sm') return Spacings.SPACING_10B;
  if (grid === 2) return Spacings.SPACING_10B;
  return Spacings.SPACING_12B;
};

const getBackgroundColor = (variant, theme, colorTheme) => {
  if (variant === 'hollow') return colorTheme.colors.white;
  if (variant === 'disabled') return colorTheme.colors.dropShadow;
  if (theme === 'red') return colorTheme.colors.red;
  if (theme === 'green') return colorTheme.colors.green;
  if (theme === 'blue') return colorTheme.colors.blue;
  return colorTheme.colors.fontGrey;
};

const getActiveBackgroundColor = (variant, theme, colorTheme) => {
  if (variant === 'hollow') return colorTheme.colors.DropShadowV2;
  if (variant === 'disabled') return colorTheme.colors.dropShadow;
  if (theme === 'green') return colorTheme.colors.darkGreen;
  if (theme === 'red') return colorTheme.colors.darkRed;
  if (theme === 'blue') return colorTheme.colors.blue;
};

const getBorderColor = (variant, theme, colorTheme) => {
  if (variant === 'filled' && theme === 'red') return colorTheme.colors.red;
  if (variant === 'filled' && theme === 'green') return colorTheme.colors.green;
  if (variant === 'filled' && theme === 'blue') return colorTheme.colors.blue;
  return colorTheme.colors.dropShadowV4;
};

const getFontSize = (grid, variant) => {
  if (grid === 2) return Fonts.SMALL;
  if (grid === 3 || grid === 4) return Fonts.LARGE;
  if (variant === 'disabled') return Fonts.LARGE;
  return Fonts.SMALL;
};

const getTextColor = (variant, theme, colorTheme) => {
  if (variant === 'filled' && theme === 'green')
    return colorTheme.colors.whiteV2;
  if (variant === 'filled') return colorTheme.colors.white;
  if (variant === 'disabled') return TextColors.DISABLED;
  if (theme === 'green') return colorTheme.colors.green;
  if (theme === 'blue') return colorTheme.colors.blue;
  if (theme === 'red') return TextColors.RED;
  if (theme === 'black') return colorTheme.colors.fontGrey;
  return colorTheme.colors.fontGrey;
};

const StyledButton = styled.button`
  position: relative;
  border-width: ${({ variant }) =>
    variant === 'hollow' && PRIMARY_BORDER_WIDTH};
  border-style: ${({ variant }) => variant === 'hollow' && SOLID_BORDER_STYLE};
  border-color: ${({ variant, ntheme, theme }) =>
    getBorderColor(variant, ntheme, theme)};
  border: ${({ variant }) =>
    (variant === 'filled' || variant === 'disabled') && 'none'};
  display: flex;
  justify-content: center;
  font-size: ${({ grid, variant }) => getFontSize(grid, variant)};
  font-weight: ${Fonts.BOLD};
  align-items: center;
  cursor: ${({ variant }) => (variant === 'disabled' ? 'no-drop' : 'pointer')};
  border-radius: ${Spacings.SPACING_1B};
  width: ${({ grid, width }) => getWidth(grid, width)};
  height: ${({ grid, maxHeight }) => getHeight(grid, maxHeight)};
  min-height: ${({ grid, maxHeight }) => getHeight(grid, maxHeight)};
  color: ${({ variant, ntheme, theme }) =>
    getTextColor(variant, ntheme, theme)};
  -webkit-user-drag: none;
  user-select: none;
  background-color: ${({ variant, ntheme, theme }) =>
    getBackgroundColor(variant, ntheme, theme)};
  transition: box-shadow 200ms ease-in-out, transform 200ms ease-in-out;
  &:hover {
    box-shadow: ${({ variant, theme }) =>
      variant !== 'disabled' && boxShadowGenerator({ theme })};
    transform: ${({ variant }) =>
      variant !== 'disabled' && 'translate(0px, -2px)'};
    border-color: ${({ variant, theme }) =>
      variant === 'hollow' && theme.colors.fontLightBlue};
  }
  &:active {
    border: ${({ variant }) => variant !== 'hollow' && 'none'};
    box-shadow: ${({ theme }) =>
      boxShadowGenerator({
        yOffset: Spacings.SPACING_2B,
        blur: Spacings.SPACING_2B,
        theme
      })};
    background-color: ${({ variant, ntheme, theme }) =>
      getActiveBackgroundColor(variant, ntheme, theme)};
  }
`;

const IconWrapper = styled.div`
  margin-right: ${({ icon }) => icon && Spacings.SPACING_2B};
  display: flex;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: inline-block;
  }
`;

const Button = forwardRef(
  (
    {
      label,
      variant,
      theme,
      onClick,
      onMouseEnter,
      onMouseLeave,
      className,
      grid,
      icon,
      type,
      maxHeight,
      dataAnalytics,
      // eslint-disable-next-line react/prop-types
      width,
      // eslint-disable-next-line react/prop-types
      boxShadow,
      btntheme
    },
    ref
  ) => {
    return (
      <StyledButton
        type={type}
        aria-label={label}
        ref={ref}
        variant={variant}
        ntheme={btntheme || theme}
        grid={grid}
        maxHeight={maxHeight}
        width={width}
        className={className}
        onClick={variant === 'disabled' ? null : onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-analytics={dataAnalytics}
        boxShadow={boxShadow}
      >
        <>
          {icon && <IconWrapper icon={icon}>{icon}</IconWrapper>}
          {label}
        </>
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;

Button.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.oneOf(['hollow', 'filled', 'disabled']),
  theme: PropTypes.oneOf(['red', 'green', 'black', 'blue']),
  btntheme: PropTypes.oneOf(['red', 'green', 'black', 'blue']),
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  icon: PropTypes.node,
  className: PropTypes.string,
  maxHeight: PropTypes.oneOf(['sm', 'xs']),
  grid: PropTypes.number,
  type: PropTypes.string,
  dataAnalytics: PropTypes.string
};

Button.defaultProps = {
  variant: 'filled',
  theme: 'green',
  btntheme: null,
  className: '',
  icon: null,
  grid: 4,
  maxHeight: null,
  onClick: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  label: '',
  type: 'button',
  dataAnalytics: ''
};
